import React, {
  FC, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { SendWhatsappModalContentComponent } from './types';
import { useAdvice } from 'hooks/useAdvice';

import useCeremonyLocation from 'hooks/useCeremonyLocation';
import { useCities } from 'hooks/useCities';
import { useVigilLocationDescription } from 'hooks/useVigilLocation';
import { formatDate } from 'utils/dateManager';

export const SendWhatsappModalContent: FC<SendWhatsappModalContentComponent> = (
  { number, recordId, onUpdateLoadingData },
) => {
  const {
    setCityCode: setVigilCity,
    setCode: setVigilLocation,
    description: vigilDescription,
  } = useVigilLocationDescription();
  const { fetchAdviceInfo, adviceInfo, adviceLoading } = useAdvice();
  const {
    getCeremonyLocation,
  } = useCeremonyLocation(adviceInfo?.ceremonyServiceDto?.ceremonyAddress?.city);
  const { getCityByNormalizedCode } = useCities();
  const { t } = useTranslation();
  const [hasVigilInfo, setHasVigilInfo] = useState<boolean>(false);
  const [hasCeremoniInfo, setHasCeremonyInfo] = useState<boolean>(false);

  useEffect(
    () => {
      if (adviceInfo?.vigilServiceDto?.address?.city && adviceInfo?.vigilServiceDto?.location) {
        setVigilCity(adviceInfo?.vigilServiceDto?.address?.city);
        setVigilLocation(adviceInfo?.vigilServiceDto?.location);
      }

      setHasVigilInfo(!!adviceInfo?.vigilServiceDto?.entryDate
        && !!adviceInfo?.vigilServiceDto?.entryTime
         && (!!adviceInfo?.vigilServiceDto?.location || !!adviceInfo?.vigilServiceDto?.vigilAtHome)
          && !!adviceInfo?.vigilServiceDto?.exitDate
           && !!adviceInfo?.vigilServiceDto?.exitTime);

      setHasCeremonyInfo(!!adviceInfo?.ceremonyServiceDto?.ceremonyDate
        && !!adviceInfo?.ceremonyServiceDto?.ceremonyTime
        && !!adviceInfo?.ceremonyServiceDto?.location);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adviceInfo],
  );

  useEffect(
    () => {
      onUpdateLoadingData(adviceLoading);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [adviceLoading],
  );

  useEffect(
    () => {
      fetchAdviceInfo(parseInt(recordId, 10));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const vigilLocationDescription = useMemo(() => {
    if (adviceInfo?.vigilServiceDto?.vigilAtHome) {
      return t('record.whatsapp.whatsappConfirmationVigil', {
        fromDate: formatDate(adviceInfo?.vigilServiceDto?.entryDate || ''),
        fromTime: adviceInfo?.vigilServiceDto?.entryTime,
        location: `${adviceInfo?.vigilServiceDto?.address?.streetName},  ${getCityByNormalizedCode(adviceInfo?.vigilServiceDto?.address?.country, adviceInfo?.vigilServiceDto?.address?.province, adviceInfo?.vigilServiceDto?.address?.city)?.description}`,
        toDate: formatDate(adviceInfo?.vigilServiceDto?.exitDate || ''),
        toTime: adviceInfo?.vigilServiceDto?.exitTime,
      });
    }

    return t('record.whatsapp.whatsappConfirmationVigil', {
      fromDate: formatDate(adviceInfo?.vigilServiceDto?.entryDate || ''),
      fromTime: adviceInfo?.vigilServiceDto?.entryTime,
      location: vigilDescription,
      toDate: formatDate(adviceInfo?.vigilServiceDto?.exitDate || ''),
      toTime: adviceInfo?.vigilServiceDto?.exitTime,
    });
  }, [
    adviceInfo?.vigilServiceDto?.address?.city,
    adviceInfo?.vigilServiceDto?.address?.country,
    adviceInfo?.vigilServiceDto?.address?.province,
    adviceInfo?.vigilServiceDto?.address?.streetName,
    adviceInfo?.vigilServiceDto?.entryDate,
    adviceInfo?.vigilServiceDto?.entryTime,
    adviceInfo?.vigilServiceDto?.exitDate,
    adviceInfo?.vigilServiceDto?.exitTime,
    adviceInfo?.vigilServiceDto?.vigilAtHome,
    getCityByNormalizedCode,
    t,
    vigilDescription]);

  return (
    <React.Fragment>
      <div>{t('record.whatsapp.whatsappConfirmation', { number })}</div>
      <div>&nbsp;</div>
      {adviceInfo && hasVigilInfo && (
        <div>
          {vigilLocationDescription}
        </div>
      )}
      {adviceInfo && !hasVigilInfo && (
        <div>
          {t('record.whatsapp.whatsappConfirmationNoVigil')}
        </div>
      )}
      {adviceInfo && hasCeremoniInfo && (
        <div>
          {t('record.whatsapp.whatsappConfirmationCeremony', {
            fromDate: formatDate(adviceInfo?.ceremonyServiceDto?.ceremonyDate || ''),
            fromTime: adviceInfo?.ceremonyServiceDto?.ceremonyTime,
            location: getCeremonyLocation(adviceInfo?.ceremonyServiceDto?.location)?.description,
          })}
        </div>
      )}
      {adviceInfo && !hasCeremoniInfo && (
        <div>
          {t('record.whatsapp.whatsappConfirmationNoCeremony')}
        </div>
      )}

    </React.Fragment>
  );
};
